import { DebouncedFunc } from 'cypress/types/lodash'
import dayjs from 'lib/dayjs'
import type { Dayjs } from 'dayjs'
import { SetStateAction } from 'react'

export const useSchedule = ({
	showToastDebounced,
	setDataCurrency,
	endDate,
	setEndDate,
	endRadio,
	dataCurrency,
	setPublisDate,
	expirationDate,
	setExpirationDate,
	publisDate,
	expirationDateEvent,
	setUntilDateEvent
}: {
	showToastDebounced: DebouncedFunc<
		(
			title: string,
			message: string,
			type: 'error' | 'success' | 'warning'
		) => void
	>
	setDataCurrency: React.Dispatch<React.SetStateAction<string | Dayjs>>
	endDate: string | Dayjs
	setEndDate: React.Dispatch<React.SetStateAction<string | Dayjs>>
	endRadio: {
		id: string
		title: string
		value: string
	}
	dataCurrency: string | Dayjs
	setPublisDate: React.Dispatch<React.SetStateAction<string | Dayjs>>
	expirationDate: string | Dayjs
	setExpirationDate: React.Dispatch<React.SetStateAction<string | Dayjs>>
	publisDate: string | Dayjs
	expirationDateEvent: string | Dayjs
	setUntilDateEvent: React.Dispatch<React.SetStateAction<string | Dayjs>>
}): {
	isValidFutureDate: (date: string | Dayjs) => boolean
	isValidExpirationDate: (
		startDate: string | Dayjs,
		expDate: string | Dayjs
	) => boolean
	isValidEventEndDate: (
		startDate: string | Dayjs,
		endEventDate: string | Dayjs
	) => boolean
	isValidEndDateVsDataCurrency: (
		startRecurrenceDate: string | Dayjs,
		endRecurrenceDate: string | Dayjs
	) => boolean
	handleChangeDataCurrency: (
		valueCurrency: SetStateAction<string | Dayjs>
	) => void
	handleChangeEndDate: (valueEndDate: SetStateAction<string | Dayjs>) => void
	isValidUntilDate: (
		endUntilEventDate: string | Dayjs,
		untilDate: string | Dayjs
	) => boolean
	handleChangeSchedule: (
		valueReleaseDate: SetStateAction<string | Dayjs>
	) => void
	handleChangeExpirationDate: (
		valueExpirationDate: SetStateAction<string | Dayjs>
	) => void
	handleChangeUntilDateEvent: (
		valueUntil: SetStateAction<string | Dayjs>
	) => void
} => {
	const isValidFutureDate = (date: string | Dayjs): boolean => {
		if (!date) return false
		const now = dayjs()
		const dateToValidate = dayjs(date as string)
		return dateToValidate.isAfter(now)
	}

	const isValidExpirationDate = (
		startDate: string | Dayjs,
		expDate: string | Dayjs
	): boolean => {
		if (!startDate || !expDate) return false
		const start = dayjs(startDate as string)
		const end = dayjs(expDate as string)
		return end.isAfter(start)
	}

	const isValidEventEndDate = (
		startDate: string | Dayjs,
		endEventDate: string | Dayjs
	): boolean => {
		if (!startDate || !endEventDate) return false
		const start = dayjs(startDate as string)
		const end = dayjs(endEventDate as string)
		return end.isAfter(start)
	}

	const isValidEndDateVsDataCurrency = (
		startRecurrenceDate: string | Dayjs,
		endRecurrenceDate: string | Dayjs
	): boolean => {
		if (!startRecurrenceDate || !endRecurrenceDate) return false
		const start = dayjs(startRecurrenceDate as string)
		const end = dayjs(endRecurrenceDate as string)
		return end.isAfter(start)
	}

	const handleChangeDataCurrency = (
		valueCurrency: SetStateAction<string | Dayjs>
	): void => {
		const now = dayjs()
		const valueDate = valueCurrency as Dayjs

		const isInvalid =
			valueDate &&
			(dayjs(valueDate).isBefore(now) || dayjs(valueDate).isSame(now))

		if (isInvalid) {
			showToastDebounced(
				'Error',
				'Oops! The recurrence start date cannot be before or equal to the current time and date.',
				'error'
			)
		} else {
			showToastDebounced.cancel()
		}

		setDataCurrency(valueDate)

		if (endDate && valueDate && endRadio.value === 'specific_day') {
			const isEndDateInvalid =
				dayjs(endDate as string).isBefore(dayjs(valueDate)) ||
				dayjs(endDate as string).isSame(dayjs(valueDate))

			if (isEndDateInvalid) {
				showToastDebounced(
					'Error',
					'Oops! The end date cannot be before or equal to the recurrence start date. Please select a later date.',
					'error'
				)
			}
		}
	}

	const handleChangeEndDate = (
		valueEndDate: SetStateAction<string | Dayjs>
	): void => {
		const now = dayjs()
		const valueDate = valueEndDate as Dayjs

		const isInvalidTime =
			valueDate &&
			(dayjs(valueDate).isBefore(now) || dayjs(valueDate).isSame(now))

		const isInvalidOrder =
			valueDate &&
			dataCurrency &&
			(dayjs(valueDate).isBefore(dayjs(dataCurrency as string)) ||
				dayjs(valueDate).isSame(dayjs(dataCurrency as string)))

		if (isInvalidTime) {
			showToastDebounced(
				'Error',
				'Oops! The end date cannot be before or equal to the current time and date.',
				'error'
			)
		} else if (isInvalidOrder) {
			showToastDebounced(
				'Error',
				'Oops! The end date cannot be before or equal to the recurrence start date. Please select a later date.',
				'error'
			)
		} else {
			showToastDebounced.cancel()
		}

		setEndDate(valueDate)
	}

	const isValidUntilDate = (
		endUntilEventDate: string | Dayjs,
		untilDate: string | Dayjs
	): boolean => {
		if (!endUntilEventDate || !untilDate) return false
		const end = dayjs(endUntilEventDate as string)
		const until = dayjs(untilDate as string)
		return until.isAfter(end)
	}

	const handleChangeSchedule = (
		valueReleaseDate: SetStateAction<string | Dayjs>
	): void => {
		const now = dayjs()
		const valueDate = valueReleaseDate as Dayjs

		const isInvalid =
			valueDate &&
			(dayjs(valueDate).isBefore(now) || dayjs(valueDate).isSame(now))

		if (isInvalid) {
			showToastDebounced(
				'Error',
				'Oops! The release date cannot be before or equal to the current time and date.',
				'error'
			)
		} else {
			showToastDebounced.cancel()
		}

		setPublisDate(valueDate)

		if (expirationDate && valueDate) {
			const isExpirationInvalid =
				dayjs(expirationDate as string).isBefore(dayjs(valueDate)) ||
				dayjs(expirationDate as string).isSame(dayjs(valueDate))

			if (isExpirationInvalid) {
				showToastDebounced(
					'Error',
					'Oops! The expiration date cannot be before or equal to the publish date. Please select a later date.',
					'error'
				)
			}
		}
	}

	const handleChangeExpirationDate = (
		valueExpirationDate: SetStateAction<string | Dayjs>
	): void => {
		const now = dayjs()
		const valueDate = valueExpirationDate as Dayjs

		const isInvalidTime =
			valueDate &&
			(dayjs(valueDate).isBefore(now) || dayjs(valueDate).isSame(now))

		const isInvalidOrder =
			valueDate &&
			publisDate &&
			(dayjs(valueDate).isBefore(dayjs(publisDate as string)) ||
				dayjs(valueDate).isSame(dayjs(publisDate as string)))

		if (isInvalidTime) {
			showToastDebounced(
				'Error',
				'Oops! The expiration date cannot be before or equal to the current time and date.',
				'error'
			)
		} else if (isInvalidOrder) {
			showToastDebounced(
				'Error',
				'Oops! The expiration date cannot be before or equal to the publish date. Please select a later date.',
				'error'
			)
		} else {
			showToastDebounced.cancel()
		}

		setExpirationDate(valueDate)
	}

	const handleChangeUntilDateEvent = (
		valueUntil: SetStateAction<string | Dayjs>
	): void => {
		const now = dayjs()
		const valueDate = valueUntil as Dayjs

		const isInvalidTime =
			valueDate &&
			(dayjs(valueDate).isBefore(now) || dayjs(valueDate).isSame(now))

		const isInvalidOrder =
			valueDate &&
			expirationDateEvent &&
			(dayjs(valueDate).isBefore(dayjs(expirationDateEvent as string)) ||
				dayjs(valueDate).isSame(dayjs(expirationDateEvent as string)))

		if (isInvalidTime) {
			showToastDebounced(
				'Error',
				'Oops! The repeat until date cannot be before or equal to the current time and date.',
				'error'
			)
		} else if (isInvalidOrder) {
			showToastDebounced(
				'Error',
				'Oops! The repeat until date cannot be before or equal to the event end date. Please select a later date.',
				'error'
			)
		} else {
			showToastDebounced.cancel()
		}

		setUntilDateEvent(valueDate)
	}

	return {
		isValidFutureDate,
		isValidExpirationDate,
		isValidEventEndDate,
		isValidEndDateVsDataCurrency,
		handleChangeDataCurrency,
		handleChangeEndDate,
		isValidUntilDate,
		handleChangeSchedule,
		handleChangeExpirationDate,
		handleChangeUntilDateEvent
	}
}
